/** Espelha `GET /v1/admin/settings` (ADR-007 D-1 e D-3). */

/** Plano que produziu o valor em vigor. */
export type SettingOrigin = 'ENVIRONMENT' | 'PLATFORM' | 'TENANT'

export type SettingType = 'STRING' | 'INT' | 'DECIMAL' | 'BOOLEAN' | 'ENUM'

export interface ResolvedSettingDto {
  key: string
  value: string
  origin: SettingOrigin
  /**
   * Travado pelo ambiente: o admin vê, mas não altera, e o PUT responde 409.
   * Destravar é decisão de devops, com registro em commit.
   */
  locked: boolean
  /**
   * O que um reset restauraria — mostrado antes de o operador resetar. Ausente quando vazio: a
   * API está com NON_NULL, então string vazia vem, mas null não.
   */
  environment_value?: string
  type: SettingType
  allowed_values: string[]
}

export interface SettingsListResponse {
  settings: ResolvedSettingDto[]
  total: number
}

/** Rótulo em português para cada plano, para não expor o enum cru na tela. */
export const ORIGIN_LABEL: Record<SettingOrigin, string> = {
  ENVIRONMENT: 'Ambiente',
  PLATFORM: 'Plataforma',
  TENANT: 'Tenant',
}

export const ORIGIN_COLOR: Record<SettingOrigin, string> = {
  ENVIRONMENT: 'gray',
  PLATFORM: 'blue',
  TENANT: 'violet',
}

/**
 * Explicação curta por parâmetro. Fica no cliente de propósito: é texto de produto, muda com mais
 * frequência que o catálogo e não deveria exigir deploy da API para ser corrigido.
 */
export const SETTING_HELP: Record<string, string> = {
  'content.destination':
    'Para onde o conteúdo do cliente pode ir. ZDR limita retenção pelo provedor, não a ' +
    'transferência — o dado ainda sai do país. Costuma vir travado pelo ambiente.',
  'llm.provider': 'openai atende qualquer endpoint OpenAI-compatible, inclusive OpenRouter.',
  'llm.model': 'Modelo usado no enriquecimento por schema.',
  'llm.base-url':
    'Vazio usa o endpoint padrão do provider. Para o piso de ZDR aponte para um ' +
    'OpenAI-compatible: os parâmetros de roteamento viajam no corpo da requisição.',
  'llm.api-key':
    'Chave de plataforma — o fallback para tenants que não trazem a própria. Entra inteira e ' +
    'volta mascarada; não há como lê-la de volta.',
  'llm.max-tokens': 'Teto de tokens por chamada de enriquecimento.',
  'audio.models':
    'Modelos de transcrição em ordem de preferência. Vazio usa só o Whisper local. Prefira ASR ' +
    'dedicado a multimodal generalista: medido em fala real, o generalista "conserta" nome ' +
    'próprio para a palavra mais provável do idioma ("da Sadia" virou "lasadinha"), e num ' +
    'catálogo é a marca que discrimina o produto. Por 1.000 áudios: qwen3-asr-flash US$ 0,25, ' +
    'gpt-transcribe US$ 0,60. A lista pode misturar os dois tipos — cada modelo é roteado para o ' +
    'endpoint que exige.',
  'whisper.model':
    'Tamanho do modelo de transcrição. Medido na VPS: tiny transcreve 10,6 s de áudio em 5,8 s ' +
    'com 2 vCPU, mas erra número e concordância. base custa +8% de tempo e acerta bem mais; ' +
    'small não roda em tempo útil nesta máquina. Só vale quando audio.models está vazio.',
  'ocr.languages':
    'Idiomas do OCR, separados por vírgula. Pedir um idioma sem o tessdata correspondente na ' +
    'imagem devolve texto vazio, sem erro.',
  'vision.models':
    'Modelos de visão em ordem de preferência, separados por vírgula. É uma lista porque modelo ' +
    'em marketplace é descontinuado sem aviso: a lista inteira vai na requisição e o roteador cai ' +
    'para o próximo em erro, limite de taxa ou indisponibilidade. Trocar a ordem aqui vale na ' +
    'próxima requisição, sem deploy.',
  'vision.confidence-threshold':
    'Abaixo disto o OCR é considerado insuficiente e a imagem escala para o VLM. Comparado com a ' +
    'confiança do OCR, e só quando ele de fato rodou — documento com camada de texto não escala.',
  'webhooks.timeout-seconds': 'Tempo limite de cada entrega de webhook.',
  'extractor.backend':
    'Motor padrão de extração. Tenants com a feature extractor_choice podem sobrepor por ' +
    'requisição, que é o que o playground usa para comparar motores.',
}
